import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { memo } from "react";
import { Collapse, Icon } from "@perxis-auto/ui-kit";
import { Button } from "@perxis-auto/ui-kit/lib/components/Button";
import { Empty } from "@perxis-auto/ui-kit/lib/components/Empty";
import { Row, Col } from "@perxis-auto/ui-kit/lib/components/Grid";
import { ModelCheckbox } from "@perxis-auto/ui-kit/lib/components/ModelCheckbox";
import { Typography } from "@perxis-auto/ui-kit/lib/components/Typography";
import clsx from "clsx";
import styles from "./ModelList.module.scss.js";
const FAKE_MODEL_OPTIONS = Array.from(Array(4)).map((_, index) => ({
  model: {
    name: `Model ${index}`
  },
  value: String(index)
}));
const ModelList = memo(function ModelList2({
  t,
  isMobile,
  isLoading,
  isShowAll,
  activeKey,
  useGroupingByBrand,
  values,
  options,
  groupedOptions,
  onShowAllButtonClick,
  onCollapseChange,
  ...props
}) {
  const isEmpty = options.length === 0 && !isLoading;
  const valuesCount = Array.isArray(values) ? values.length : 0;
  if (isEmpty) {
    return /* @__PURE__ */ jsx(
      Empty,
      {
        title: t("widgets.modelList.missingData.title"),
        description: /* @__PURE__ */ jsx(Fragment, { children: t("widgets.modelList.missingData.tryAgainLater") })
      }
    );
  }
  const modelGroup = isMobile ? /* @__PURE__ */ jsx(
    Collapse,
    {
      activeKey,
      onChange: onCollapseChange,
      items: groupedOptions.map(([brand, { value = [], options: options2 = [], ...groupedProps }]) => {
        return {
          key: brand.id,
          label: /* @__PURE__ */ jsxs("div", { className: styles.modelList__brandHeader, children: [
            brand.imgSrc ? /* @__PURE__ */ jsx("img", { src: brand.imgSrc, alt: brand.imgAlt, className: styles.modelList__brandLogo }) : /* @__PURE__ */ jsx(Typography.Title, { level: "h3", color: "darkest", className: styles.modelList__brandTitle, children: brand.title }),
            !!brand.description && /* @__PURE__ */ jsx(Typography.Text, { level: "main", color: "dark-light", className: styles.modelList__brandDescription, children: brand.description })
          ] }),
          children: /* @__PURE__ */ jsx(
            ModelCheckbox.Group,
            {
              isLoading,
              layout: {
                xs: "compact",
                sm: "full"
              },
              gutter: [
                {
                  xs: 0,
                  sm: 18
                },
                {
                  xs: 8,
                  sm: 16
                }
              ],
              xs: 24,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 6,
              xxl: 6,
              checkboxLayout: "checkbox",
              className: clsx(styles.modelList__checkbox, {
                [styles["modelList__checkbox--loading"]]: isLoading
              }),
              ...props,
              ...groupedProps,
              value,
              options: isLoading ? FAKE_MODEL_OPTIONS : options2
            }
          )
        };
      }),
      expandIcon: ({ isActive }) => /* @__PURE__ */ jsx(Icon, { name: isActive ? "minus" : "plus", sticker: "model-group-expand-icon", size: 20 })
    }
  ) : /* @__PURE__ */ jsx(Row, { gutter: [0, "vertical-orange"], children: groupedOptions.map(([brand, { value = [], options: options2 = [], ...groupedProps }]) => {
    return /* @__PURE__ */ jsx(Col, { span: 24, children: /* @__PURE__ */ jsxs(Row, { gutter: [0, "vertical-green"], children: [
      /* @__PURE__ */ jsx(Col, { span: 24, children: /* @__PURE__ */ jsxs("div", { className: styles.modelList__brandHeader, children: [
        brand.imgSrc ? /* @__PURE__ */ jsx("img", { src: brand.imgSrc, alt: brand.imgAlt, className: styles.modelList__brandLogo }) : /* @__PURE__ */ jsx(Typography.Title, { level: "h3", color: "darkest", className: styles.modelList__brandTitle, children: brand.title }),
        !!brand.description && /* @__PURE__ */ jsx(Typography.Text, { level: "main", color: "dark-light", className: styles.modelList__brandDescription, children: brand.description })
      ] }) }),
      /* @__PURE__ */ jsx(Col, { span: 24, children: /* @__PURE__ */ jsx(
        ModelCheckbox.Group,
        {
          isLoading,
          layout: {
            xs: "compact",
            sm: "full"
          },
          gutter: [
            {
              xs: 0,
              sm: 18
            },
            {
              xs: 8,
              sm: 16
            }
          ],
          xs: 24,
          sm: 12,
          md: 12,
          lg: 8,
          xl: 6,
          xxl: 6,
          checkboxLayout: "checkbox",
          className: clsx(styles.modelList__checkbox, {
            [styles["modelList__checkbox--loading"]]: isLoading
          }),
          ...props,
          ...groupedProps,
          value,
          options: isLoading ? FAKE_MODEL_OPTIONS : options2
        }
      ) })
    ] }) }, brand.id);
  }) });
  return /* @__PURE__ */ jsx("div", { className: styles.modelList, children: /* @__PURE__ */ jsxs(Row, { gutter: [0, "vertical-yellow"], justify: "center", children: [
    /* @__PURE__ */ jsx(Col, { span: 24, children: /* @__PURE__ */ jsxs(Row, { gutter: [0, "vertical-yellow"], justify: "start", children: [
      /* @__PURE__ */ jsx(Col, { span: 24, children: /* @__PURE__ */ jsx(Typography.Title, { headingLevel: 3, level: "h2", color: "darkest", className: styles.modelList__title, children: t("widgets.modelList.title") }) }),
      useGroupingByBrand ? /* @__PURE__ */ jsx(Col, { span: 24, children: modelGroup }) : /* @__PURE__ */ jsx(Col, { span: 24, children: /* @__PURE__ */ jsx(
        ModelCheckbox.Group,
        {
          isLoading,
          layout: {
            xs: "compact",
            sm: "full"
          },
          gutter: [
            {
              xs: 0,
              sm: 18
            },
            {
              xs: 8,
              sm: 16
            }
          ],
          xs: 24,
          sm: 12,
          md: 12,
          lg: 8,
          xl: 6,
          xxl: 6,
          checkboxLayout: "checkbox",
          className: clsx(styles.modelList__checkbox, {
            [styles["modelList__checkbox--loading"]]: isLoading
          }),
          ...props,
          value: values,
          options: isLoading ? FAKE_MODEL_OPTIONS : options
        }
      ) })
    ] }) }),
    !isLoading && /* @__PURE__ */ jsx(Col, { span: 24, children: /* @__PURE__ */ jsx(Row, { justify: "center", children: /* @__PURE__ */ jsx(Col, { children: /* @__PURE__ */ jsx(Button, { type: "primary", onClick: onShowAllButtonClick, children: !isShowAll ? `${t("widgets.modelList.show")} (${valuesCount})` : t("widgets.modelList.showAll") }) }) }) })
  ] }) });
});
export {
  ModelList
};
